import { template } from "@ember/template-compiler";
import Component from '@glimmer/component';
import { buttonClassName } from '../button.utils';
import type { BaseButtonProps } from '../button.utils';
import { sprinkles } from '@/css/sprinkles.css';
import clsx from 'clsx';
import { extractProps } from '@/utils/vanilla-extract-props';
import PlumaBox from '../../box/ember/index';
import type { BoxSignature } from '../../box/ember/index';
import PlumaIcon from '../../icon/ember/index';
import { consume } from 'ember-provide-consume-context';
import type ContextRegistry from 'ember-provide-consume-context/context-registry';
import { PlumaContext } from '../../pluma-provider/ember/index';
// @ts-ignore
import { on } from '@ember/modifier';
export interface ButtonSignature<T extends string = 'button'> {
    Element: BoxSignature<T>['Element'];
    Args: BoxSignature<T>['Args'] & BaseButtonProps;
    Blocks: {
        default: [];
    };
}
export default class Button<T extends string = 'button'> extends Component<ButtonSignature<T>> {
    @consume(PlumaContext)
    plumaContext: ContextRegistry[typeof PlumaContext];
    get as() {
        return (this.args.as ?? 'button') as any;
    }
    get argsWithDefaults() {
        const { variant: variant1, size: size1, isError: isError1, icon: icon1, iconPosition: iconPosition1 = 'leading', isIconOnly: isIconOnly1 = false, isDisabled: isDisabled1 = false, unsafe_isLegacy: unsafe_isLegacy1 = this.plumaContext?.componentFlags?.unsafe_useLegacyButton ?? false, ...restArgs1 } = this.args;
        return {
            variant: variant1,
            size: size1,
            isError: isError1,
            icon: icon1,
            iconPosition: iconPosition1,
            isIconOnly: isIconOnly1,
            isDisabled: isDisabled1,
            unsafe_isLegacy: unsafe_isLegacy1,
            ...restArgs1
        };
    }
    get className() {
        const { variant: variant1, size: size1, isError: isError1, icon: icon1, iconPosition: iconPosition1, isIconOnly: isIconOnly1, unsafe_isLegacy: unsafe_isLegacy1, ...restArgs1 } = this.argsWithDefaults;
        // Until something like https://github.com/emberjs/rfcs/pull/593 lands in
        // Ember, we can't forward arbitrary args to the PlumaBox component.
        // Instead, we need to extract sprinkle arguments and get the class names in
        // each Ember component.
        const { sprinkleProps: sprinkleProps1 } = extractProps(restArgs1);
        return clsx(buttonClassName({
            variant: variant1,
            size: size1,
            isError: isError1,
            icon: icon1,
            iconPosition: iconPosition1,
            isIconOnly: isIconOnly1,
            unsafe_isLegacy: unsafe_isLegacy1
        }), sprinkles(sprinkleProps1));
    }
    get iconPosition() {
        return this.argsWithDefaults.iconPosition;
    }
    get iconSize() {
        return this.args.size === 'small' ? 'sm' : 'md';
    }
    get isIconOnly() {
        return this.argsWithDefaults.isIconOnly && this.args.icon != null;
    }
    get hasLeadingIcon() {
        return this.args.icon != null && this.iconPosition === 'leading';
    }
    get hasTrailingIcon() {
        return this.args.icon != null && this.iconPosition === 'trailing';
    }
    get type() {
        if (this.as !== 'button') {
            return undefined;
        }
        return 'button';
    }
    get tabIndex() {
        return this.argsWithDefaults.isDisabled ? -1 : undefined;
    }
    get disabled() {
        return this.argsWithDefaults.isDisabled === true ? true : undefined;
    }
    maybeBlockClick = (e1: MouseEvent)=>{
        // We block the click in the capture phase, because modifer order in Ember
        // is not guaranteed, but we want to stop a consumer's on click handler from
        // being called if the button is disabled.
        if (this.argsWithDefaults.isDisabled) {
            e1.preventDefault();
            e1.stopPropagation();
        }
    };
    static{
        template(`<PlumaBox
		@as={{this.as}}
		class={{this.className}}
		type={{this.type}}
		{{on "click" this.maybeBlockClick capture=true}}
		tabindex={{this.tabIndex}}
		{{! @glint-expect-error }}
		...attributes
		disabled={{this.disabled}}
		aria-disabled={{this.disabled}}
	>
		{{~#if this.isIconOnly~}}
			{{! @glint-expect-error @icon is not undefined at this point }}
			<PlumaIcon @name={{@icon}} @size={{this.iconSize}} />
		{{~else~}}
			{{~#if this.hasLeadingIcon~}}
				{{! @glint-expect-error @icon is not undefined at this point }}
				<PlumaIcon @name={{@icon}} @size={{this.iconSize}} />
			{{~/if~}}
			{{~yield~}}
			{{~#if this.hasTrailingIcon~}}
				{{! @glint-expect-error @icon is not undefined at this point }}
				<PlumaIcon @name={{@icon}} @size={{this.iconSize}} />
			{{~/if~}}
		{{~/if~}}
	</PlumaBox>`, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
}
